import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMessage, fileAuthUrl } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Card } from "@/pages/portal/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { UserPlus, Eye, KeyRound, Pencil, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  admin: "bg-navy text-white",
  rep: "bg-electric/10 text-electric",
  manager: "bg-amber-50 text-amber-700",
  customer: "bg-slate-100 text-slate-600",
};

const ADMIN_FILTERS = ["", "admin", "manager", "rep", "customer"];
const MANAGER_FILTERS = ["", "manager", "rep"];

export default function Team() {
  const { user, impersonate } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin";
  const isManager = user.role === "manager";
  const filters = isAdmin ? ADMIN_FILTERS : isManager ? MANAGER_FILTERS : [];
  const [users, setUsers] = useState([]);
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.length && roleFilter) params.role = roleFilter;
      const { data } = await api.get("/api/portal/users", { params });
      setUsers(data.users);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  }, [filters.length, roleFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/api/portal/reps").then((r) => setReps(r.data.reps)).catch(() => {}); }, []);

  const actAs = async (u) => {
    try {
      await impersonate(u.id);
      navigate("/portal");
      toast.success(`Acting as ${u.name}`);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="team-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((r) => (
              <button
                key={r || "all"}
                onClick={() => setRoleFilter(r)}
                className={cn("rounded-full border px-3 py-1 text-xs font-medium capitalize", roleFilter === r ? "border-navy bg-navy text-white" : "border-line bg-white text-slatey hover:border-navy/40")}
                data-testid={`role-filter-${r || "all"}`}
              >
                {r || "All"}{r ? "s" : ""}
              </button>
            ))}
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-electric text-white hover:bg-electric-hover" data-testid="add-user-btn">
          <UserPlus className="mr-1.5 h-4 w-4" /> {isAdmin ? "Add user" : isManager ? "Add rep" : "Add sub-rep"}
        </Button>
      </div>

      <Card>
        {loading ? (
          <div className="p-10 text-center text-sm text-slatey">Loading…</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-sm text-slatey">{isAdmin ? "No users yet." : isManager ? "No reps yet." : "You haven't added any team members yet."}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b border-line text-xs uppercase tracking-wide text-slatey">
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  {(isAdmin || isManager) && <th className="px-5 py-3 font-medium">Reports to</th>}
                  <th className="px-5 py-3 font-medium">Company</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        {u.profile_picture_url ? (
                          <img src={fileAuthUrl(u.profile_picture_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-ice text-xs font-semibold text-slatey">
                            {(u.name || "?")[0]?.toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-navy">{u.name}</p>
                          <p className="text-xs text-slatey">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={cn("rounded px-2 py-0.5 text-[11px] font-medium capitalize", ROLE_BADGE[u.role])}>{u.role}</span>
                    </td>
                    {(isAdmin || isManager) && (
                      <td className="px-5 py-3 text-slatey" data-testid={`user-upline-${u.id}`}>{(u.manager_ids || []).map((id) => reps.find((r) => r.id === id)?.name).filter(Boolean).join(", ") || "—"}</td>
                    )}
                    <td className="px-5 py-3 text-slatey">{u.company_name || "—"}</td>
                    <td className="px-5 py-3">
                      <span className={cn("text-xs font-medium", u.active ? "text-emerald-600" : "text-red-600")}>{u.active ? "Active" : "Inactive"}</span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {isAdmin && u.role !== "admin" && (
                          <button onClick={() => actAs(u)} title="Act as" className="rounded p-1.5 text-slatey hover:bg-ice hover:text-navy" data-testid={`act-as-${u.id}`}>
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        {isAdmin && (
                          <>
                            <button onClick={() => setResetUser(u)} title="Reset password" className="rounded p-1.5 text-slatey hover:bg-ice hover:text-navy" data-testid={`reset-pw-${u.id}`}>
                              <KeyRound className="h-4 w-4" />
                            </button>
                            <button onClick={() => setEditUser(u)} title="Edit" className="rounded p-1.5 text-slatey hover:bg-ice hover:text-navy" data-testid={`edit-user-${u.id}`}>
                              <Pencil className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {createOpen && (
        <CreateUserDialog isAdmin={isAdmin} isManager={isManager} reps={reps} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); load(); }} />
      )}
      {editUser && (
        <EditUserDialog user={editUser} reps={reps} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />
      )}
      {resetUser && (
        <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} onDone={() => setResetUser(null)} />
      )}
    </div>
  );
}

function CreateUserDialog({ isAdmin, isManager, reps, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", password: "", role: "rep", title: "", phone: "", company_name: "", manager_ids: [] });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...f };
      if (!isAdmin) delete payload.role;
      await api.post("/api/portal/users", payload);
      toast.success("User created");
      onDone();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle>{isAdmin ? "Add user" : isManager ? "Add rep" : "Add sub-rep"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="cu-name" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="cu-email" /></div>
          <div><Label>Temporary password</Label><Input value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="cu-password" placeholder="min 8 characters" /></div>
          {isAdmin && (
            <div>
              <Label>Role</Label>
              <select value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="cu-role" className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm">
                <option value="rep">Rep</option>
                <option value="manager">Manager</option>
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Title</Label><Input value={f.title} onChange={(e) => set("title", e.target.value)} /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          </div>
          <div><Label>Company</Label><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
          {isAdmin && ["rep", "manager"].includes(f.role) && (
            <UplinePicker reps={reps} value={f.manager_ids} onChange={(v) => set("manager_ids", v)} testid="cu-uplines" />
          )}
          {isManager && (
            <div>
              <Label>Reports to (upline rep)</Label>
              <select value={f.manager_ids[0] || ""} onChange={(e) => set("manager_ids", e.target.value ? [e.target.value] : [])} data-testid="cu-upline" className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm">
                <option value="">Me</option>
                {reps.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
              </select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-electric text-white hover:bg-electric-hover" data-testid="cu-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, reps, onClose, onDone }) {
  const [f, setF] = useState({ name: user.name, role: user.role, active: user.active, manager_ids: user.manager_ids || [], company_name: user.company_name || "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/api/portal/users/${user.id}`, f);
      toast.success("User updated");
      onDone();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle>Edit {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
          <div>
            <Label>Role</Label>
            <select value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="eu-role" className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm">
              <option value="rep">Rep</option>
              <option value="manager">Manager</option>
              <option value="customer">Customer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          {["rep", "manager"].includes(f.role) && (
            <UplinePicker reps={reps.filter((r) => r.id !== user.id)} value={f.manager_ids} onChange={(v) => set("manager_ids", v)} testid="eu-uplines" />
          )}
          <div><Label>Company</Label><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} /></div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => set("active", e.target.checked)} data-testid="eu-active" className="h-4 w-4 accent-electric" />
            Active account
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-electric text-white hover:bg-electric-hover" data-testid="eu-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UplinePicker({ reps, value, onChange, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div>
      <Label>Reports to (select one or more uplines)</Label>
      <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-line bg-white p-2" data-testid={testid}>
        {reps.length === 0 && <p className="px-1 py-1 text-xs text-slatey">No reps or managers yet.</p>}
        {reps.map((r) => (
          <label key={r.id} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-ice">
            <input type="checkbox" checked={value.includes(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 accent-electric" data-testid={`${testid}-${r.id}`} />
            <span className="text-navy">{r.name}</span>
            <span className="text-xs capitalize text-slatey">{r.role}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ResetPasswordDialog({ user, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/api/portal/users/${user.id}/reset-password`, { new_password: pw });
      toast.success("Password reset");
      onDone();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="reset-pw-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-energy" /> Reset password</DialogTitle></DialogHeader>
        <p className="text-sm text-slatey">Set a new password for <span className="font-medium text-navy">{user.name}</span>.</p>
        <Input value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password (min 8)" data-testid="reset-pw-input" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || pw.length < 8} className="bg-electric text-white hover:bg-electric-hover" data-testid="reset-pw-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
